import superagentPromise from 'superagent-promise';
import _superagent from 'superagent';
import isIp from 'is-ip';

const DEFAULT_RETRY_COUNT = 2;

const DEFAULT_TIMEOUT = {
    response: 10000,  // server to start sending timeout (10sec)
    deadline: 60000, // max time for the connection timeout (1min)
};

const FILE_UPLOAD_TIMEOUT = {
    response: 10000,  // server to start sending timeout (10sec)
    deadline: 120000, // max time for the connection timeout (2min)
};

const superagent = superagentPromise(_superagent, global.Promise);

const domainToBackend: { [key: string]: string } = {
    "the.select": "selectdc.net",
    "select.id": "selectdc.net",
    "select.community": "selectdc.net",
    "eks.selectdc.net": "getselect.co",
};

function getDomainName(hostName = window.location.hostname) {
    if (hostName === 'csb.eks.selectdc.net') {
        // Special case that will be removed after finish infra changes
        return 'eks.selectdc.net';
    }
    return hostName.substring(hostName.lastIndexOf(".", hostName.lastIndexOf(".") - 1) + 1);
}

const getBackend = () => {
    const domain = getDomainName();
    return domainToBackend[domain] || domain;
};

export function isLocalHost() {
    return window.location.hostname === 'localhost' || isIp(window.location.hostname);
}

export function getCSBURL() {
    if (isLocalHost()) {
        return `${window.location.protocol}//${window.location.hostname}:3000`;
    }

    return `${window.location.protocol}//csb.${getDomainName()}`;
}

export function getPhoenixURL() {
    if (isLocalHost()) {
        return `${window.location.protocol}//${window.location.hostname}:4000`;
    }
    // Here we use host domain name!
    return `${window.location.protocol}//phoenix.${getBackend()}`;
}

function getPhoenixURLAPI() {
    // return `https//staging.selectdc1.com/v1`;
    // return `https//staging.selectdc1.com/v1`;
    if (isLocalHost()) {
        return `${window.location.protocol}//${window.location.hostname}:4000/v1`;
        // return `${window.location.protocol}//staging.selectdc1.com/v1`;
    }
    if (!isProduction) {
        // hardcoded route that support pathx
        return `${window.location.protocol}//staging.selectdc1.com/v1`;
    }
    // Here we use backend domain name!
    return `${window.location.protocol}//phoenix.${getBackend()}/v1`;
}

export const isProduction = getBackend() === 'selectdc.net';

function getFileStoreURL() {
  if (isLocalHost()) {
    return `${window.location.protocol}//${window.location.hostname}:7000/api/v1`;
  }
  if (!isProduction) {
    return `${window.location.protocol}//filestore-api.getselect.co/api/v1`;
  }
  return `${window.location.protocol}//filestore-api.selectdc.net/api/v1`;
}

export const API_ROOT = process.env.REACT_APP_PHOENIX_API_ROOT || getPhoenixURLAPI();
export const FILESTORE_API_ROOT = process.env.REACT_APP_FILESTORE_API_ROOT || getFileStoreURL();

const responseBody = (res: _superagent.Response) => res.body;
const encode = encodeURIComponent;

let token: string = '';
const tokenPlugin = (req: _superagent.Request) => {
    if (token) {
        req.set('authorization', `Bearer ${token}`);
    }
    req.set('Accept', 'application/json');
    req.set('Content-Type', 'application/json');
    req.set('content-language', 'en');
};

const requests = {
    del: (url: string) =>
        superagent.del(`${API_ROOT}${url}`)
            .timeout(DEFAULT_TIMEOUT)
            .retry(DEFAULT_RETRY_COUNT)
            .use(tokenPlugin)
            .then(responseBody),
    get: (url: string) =>
        superagent.get(`${API_ROOT}${url}`)
            .timeout(DEFAULT_TIMEOUT)
            .retry(DEFAULT_RETRY_COUNT)
            .use(tokenPlugin)
            .then(responseBody),
    post: (url: string, body: Object) => {
        return superagent.post(`${API_ROOT}${url}`, body)
            .timeout(DEFAULT_TIMEOUT)
            .retry(DEFAULT_RETRY_COUNT)
            .use(tokenPlugin)
            .then(responseBody);
    }

};

const fsTokenPlugin = (req: _superagent.Request) => {
    req.set('token', `1:6652a0d2ad01182901b0`); // token only working for filestore
    req.set('uid', `103575`); // token only working for filestore
};

const fsRequests = {
    del: (url: string) => superagent.del(`${FILESTORE_API_ROOT}${url}`)
        .timeout(FILE_UPLOAD_TIMEOUT)
        .retry(DEFAULT_RETRY_COUNT)
        .use(fsTokenPlugin).then(responseBody),
    get: (url: string) => superagent.get(`${FILESTORE_API_ROOT}${url}`)
        .timeout(FILE_UPLOAD_TIMEOUT)
        .retry(DEFAULT_RETRY_COUNT)
        .use(fsTokenPlugin)
        .then(responseBody),
    post: (url: string, body: Object) => superagent.post(`${FILESTORE_API_ROOT}${url}`, body)
        .timeout(FILE_UPLOAD_TIMEOUT)
        .retry(DEFAULT_RETRY_COUNT)
        .use(fsTokenPlugin)
        .then(responseBody)
};

const Filestore = {
    uploadUrl: (url: string) => fsRequests.post(`/upload/url`, {
      source_url: url
    }),
    uploadFile: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return fsRequests.post('/upload/file', formData)
    }
};

const Auth = {
    checkPhoneNumber: (countryCode: string, phoneNumber: string) =>
        requests.post('/check-phone-number', {
            number: `${countryCode}${phoneNumber}`,
        }),
    requestVerificationCode: (countryCode: string, phoneNumber: string) =>
        requests.post('/request-sms-token', {
            number: `${countryCode}${phoneNumber}`,
            cc: countryCode,
            v2: true
        }),
    verifyPhoneNumberAndVerificationCode: (countryCode: string, phoneNumber: string, token: string) =>
        requests.post('/verify-sms-token', {
            is_csb: true,
            number: `${countryCode}${phoneNumber}`,
            cc: countryCode,
            token,
            v2: true
        }),
    getJwt: (refresh_token: string) =>
        requests.post('/auth/token', {
            is_csb: true,
            refresh_token,
        })
};

const Invitation = {
    detectHarmfulWords: (value: string) =>
        requests.post('/community-builder/detect-harmful-words', {
            value
        }),
    sendEmailInvitation: (email: string, groupId: string, deeplink: string) =>
        requests.post('/community-builder/send-invite', {
            email: email,
            group_id: groupId,
            deeplink: deeplink
        }),
    fetchLink: (body: {link_id: string} |  {select_id: string})=>
        requests.post('/community-builder/user-deeplink', body),
};

const Group = {
    isSelectIdAvailable: (selectId: string) => requests.post('/group/select-id-available', {select_id: selectId}),
    addWelcomePost: (payload: any) => requests.post('/group/featured-post/new', {payload}),
    addGroupBio: (group_id: string, description: string) => requests.post('/group/update/description', {group_id, description}),
    addFeatureGroups: (group_id: string, featured_group_ids: Array<string>) => requests.post('/group/featured/new', {
        group_id,
        featured_group_ids
    })
};

type IntroScreenData = {
    title?: string,
    subtitle?: string,
    image?: string,
    backgroundImage?: string,
    backgroundColor?: string,
    fontColor?: string,
}

type DeeplinkData = {
    backgroundColor: string, // use in landing and loading screen
    fontColor?: string, // use in landing and loading screen
    logo?: string, // use in landing
    tagline?: string, // use in landing
    backgroundImage?: string, // use in landing
    introScreens: Array<IntroScreenData>
}

const Deeplink = {

    createDeeplinkAuthenticated: (
        groupId: string,
        deeplinkData: DeeplinkData,
        email: string,
        expiration: string,
        ogDescription: string,
        is_inviter: boolean,
        is_private: boolean,
        extraGroupIds: Array<string>,
        setDefault: boolean,
        all_texts: string

    ) =>
        requests.post('/community-builder/create-deeplink', {
            channel: 'custom-skin-builder',
            group_id: groupId,
            deeplink_data: deeplinkData,
            expiration: expiration ? parseInt(expiration, 10) : null,
            email,
            og_description: ogDescription,
            group_ids: [groupId, ...extraGroupIds],
            is_inviter,
            is_private,
            set_default: setDefault,
            all_texts
        }),

    createDeeplinkWithNewGroup: (
        groupId: string,
        groupName: string,
        groupAvatar: string,
        deeplinkData: DeeplinkData,
        email: string,
        expiration: string,
        ogDescription: string,
        is_inviter: boolean,
        is_private: boolean,
        extraGroupIds: Array<string>,
        setDefault: boolean,
        all_texts: string,
        allow_add_user: boolean,
        allow_send_post: boolean,
        show_history_from: boolean,
        share_to_public: boolean,
        allow_cross_exposure: boolean,
        groupSelectId?: string
    ) =>
        requests.post('/community-builder/create-deeplink-open', {
            channel: 'custom-skin-builder',
            group_id: groupId,
            group_name: groupName,
            group_avatar: groupAvatar,
            deeplink_data: deeplinkData,
            expiration: expiration ? parseInt(expiration, 10) : null,
            email,
            og_description: ogDescription,
            group_ids: [groupId, ...extraGroupIds],
            is_inviter,
            is_private,
            select_id: groupSelectId,
            set_default: setDefault,
            allow_add_user,
            allow_send_post,
            show_history_from,
            share_to_public,
            allow_cross_exposure,
            all_texts
        }),

    editDeeplink: (
        link_id: string,
        group_id: string,
        deeplink_data: DeeplinkData,
        set_default: boolean,
        expiration: string,
        og_description: string,
        is_inviter: boolean,
        is_private: boolean,
        extraGroupIds: Array<string>,
        all_texts: string,
        email: string
    ) =>
        requests.post('/community-builder/edit-deeplink', {
            link_id,
            is_private,
            group_id,
            deeplink_data,
            og_description,
            expiration: expiration ? parseInt(expiration, 10) : null,
            is_inviter,
            set_default,
            group_ids: [group_id, ...extraGroupIds],
            all_texts,
            email
        }),
};

export default {
    Auth,
    Invitation,
    Deeplink,
    Filestore,
    setToken: (_token: string) => {
        token = _token;
    },
    Group
};
